import { normalizeDpgfLineValues, readDpgfNumber } from './quoteDpgfNormalization';

const text = (value) => String(value ?? '').trim();

const normalizedHeader = (value) => text(value).toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ').trim();

const aliases = {
  code: ['n', 'no', 'numero', 'num', 'repere', 'poste', 'article', 'reference'],
  description: ['designation', 'libelle', 'description', 'ouvrage', 'prestations', 'prestation'],
  unit: ['unite', 'u', 'unit'],
  quantity: ['quantite', 'qte', 'qty'],
  unitPrice: ['prix unitaire', 'pu', 'p u', 'prix u'],
  total: ['total', 'montant', 'prix total'],
};

export const detectDpgfColumns = (headers = []) => {
  const result = {};
  headers.forEach((header, index) => {
    const normalized = normalizedHeader(header);
    Object.entries(aliases).forEach(([field, candidates]) => {
      if (result[field] !== undefined) return;
      if (candidates.some((candidate) => normalized === candidate || normalized.includes(candidate))) result[field] = index;
    });
  });
  return result;
};

const codeDepth = (code) => {
  const value = text(code);
  if (!value) return null;
  const pieces = value.split(/[.\-_/]/).filter(Boolean);
  if (pieces.length > 1) return pieces.length - 1;
  return null;
};

const indentationDepth = (cell) => {
  const alignment = cell?.alignment || {};
  const indent = Number(alignment.indent || 0);
  return Number.isFinite(indent) ? indent : 0;
};

export const workbookToRows = async (file) => {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets[0];
  if (!worksheet) throw new Error('Le classeur DPGF ne contient aucune feuille.');
  const matrix = [];
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    const cells = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cells[colNumber - 1] = cell.text ?? cell.value ?? '';
    });
    matrix.push({ rowNumber, cells, row });
  });
  if (!matrix.length) throw new Error('La feuille DPGF est vide.');
  return { worksheetName: worksheet.name, matrix };
};

export const guessHeaderRow = (matrix) => {
  let best = { index: 0, score: -1, columns: {} };
  matrix.slice(0, 30).forEach((row, index) => {
    const columns = detectDpgfColumns(row.cells);
    const score = Object.keys(columns).length + (columns.description !== undefined ? 3 : 0);
    if (score > best.score) best = { index, score, columns };
  });
  return best;
};

const makeId = (prefix, rowNumber) => `${prefix}-${Date.now()}-${rowNumber}-${Math.random().toString(16).slice(2)}`;

export const buildSectionsFromDpgf = ({ matrix, headerIndex, columns, defaultVatRate = 20 }) => {
  const rows = matrix.slice(headerIndex + 1);
  const sections = [];
  const sectionStack = [];
  let defaultSection = null;
  const stats = { sections: 0, lines: 0, texts: 0, warnings: [], rejectedLines: 0, errors: [] };

  const ensureDefault = () => {
    if (defaultSection) return defaultSection;
    defaultSection = {
      id: makeId('dpgf-section', 0), parentSectionId: null, code: '', title: 'DPGF', description: '',
      isClientVisible: true, orderIndex: sections.length, lines: [],
    };
    sections.push(defaultSection); stats.sections += 1;
    return defaultSection;
  };

  rows.forEach(({ rowNumber, cells, row }) => {
    const description = text(cells[columns.description]);
    const code = columns.code !== undefined ? text(cells[columns.code]) : '';
    const unit = columns.unit !== undefined ? text(cells[columns.unit]) : '';
    const quantityRaw = columns.quantity !== undefined ? cells[columns.quantity] : undefined;
    const unitPriceRaw = columns.unitPrice !== undefined ? cells[columns.unitPrice] : undefined;
    const totalRaw = columns.total !== undefined ? cells[columns.total] : undefined;
    if (!description && !code && !unit && !text(quantityRaw) && !text(unitPriceRaw) && !text(totalRaw)) return;

    const hasCommercialData = Boolean(unit || text(quantityRaw) || text(unitPriceRaw) || text(totalRaw));
    const depthFromCode = codeDepth(code);
    const descriptionCell = columns.description !== undefined ? row?.getCell?.(columns.description + 1) : null;
    const depth = depthFromCode ?? indentationDepth(descriptionCell);

    const looksLikeSection = !hasCommercialData && description && Boolean(
      code || depthFromCode !== null || descriptionCell?.font?.bold
    );
    if (looksLikeSection) {
      const section = {
        id: makeId('dpgf-section', rowNumber),
        parentSectionId: depth > 0 ? sectionStack[depth - 1]?.id || null : null,
        code,
        title: description,
        description: '',
        isClientVisible: true,
        orderIndex: sections.filter((item) => String(item.parentSectionId || '') === String(depth > 0 ? sectionStack[depth - 1]?.id || '' : '')).length,
        lines: [],
      };
      sections.push(section);
      sectionStack[depth] = section;
      sectionStack.splice(depth + 1);
      stats.sections += 1;
      return;
    }

    if (!hasCommercialData && (description || code)) {
      const target = [...sectionStack].reverse().find(Boolean) || ensureDefault();
      target.lines.push({
        id: makeId('dpgf-text', rowNumber), quoteSectionId: target.id, description: description || code,
        lineComment: '', lineType: 'text', quantity: 0, unit: 'Ens', estimatedHours: 0, laborRate: 0,
        purchaseCost: 0, subcontractingCost: 0, marginCoefficient: 1, vatRate: 0,
        weightingCoefficient: 1, isClientVisible: true,
      });
      stats.texts += 1;
      return;
    }

    const values = normalizeDpgfLineValues({ quantity: quantityRaw, unitPrice: unitPriceRaw, total: totalRaw, rowNumber });
    if (!values.importable) {
      stats.rejectedLines += 1;
      stats.errors.push({ rowNumber, source: values.source, diagnostics: values.diagnostics });
      return;
    }
    values.diagnostics.forEach((item) => stats.warnings.push(`Ligne ${rowNumber} : ${item.message}`));
    const target = [...sectionStack].reverse().find(Boolean) || ensureDefault();
    target.lines.push({
      id: makeId('dpgf-line', rowNumber), quoteSectionId: target.id, code,
      description: description || code || `Ligne DPGF ${rowNumber}`, lineComment: '', lineType: 'service',
      quantity: values.quantity, unit: ['Ens', 'U', 'm', 'ml'].includes(unit) ? unit : (unit || 'Ens'),
      unitPrice: values.unitPrice, purchaseCost: 0, subcontractingCost: 0, estimatedHours: 0, laborRate: 0,
      marginCoefficient: 1, inheritedCoefficient: 1, calculatedCoefficient: 1, effectiveCoefficient: 1,
      coefficientMode: 'inherited', sellingPriceMode: values.sellingPriceMode,
      forcedUnitSellingPrice: values.forcedUnitSellingPrice,
      ...(values.forceReason ? { forceReason: values.forceReason } : {}),
      sourceCatalogReference: `dpgf:row:${rowNumber}`,
      dpgfSource: { ...values.source, diagnostics: values.diagnostics },
      vatRate: defaultVatRate, discountKind: 'percent', discountValue: 0,
      weightingCoefficient: 1, isClientVisible: true,
    });
    if (unit && !['Ens', 'U', 'm', 'ml'].includes(unit)) stats.warnings.push(`Ligne ${rowNumber} : unité « ${unit} » normalisée par le moteur.`);
    stats.lines += 1;
  });

  return { sections, stats };
};

// Builds the explicit B6 source contract from the existing workbook reader.
// Only cell normalization occurs here; B6/B1/B2 validate and calculate server-side.
export const buildCompositionDpgfSource = ({ matrix, headerIndex, columns, defaultVatRate, knownZeroCosts = false }) => {
  const exact = (raw, field) => {
    const parsed = readDpgfNumber(raw);
    if (parsed.state !== 'valid' || parsed.decimal.startsWith('-')) throw new Error(`${field} doit être explicite et positif ou nul.`);
    return parsed.decimal;
  };
  const sourceRows = matrix.slice(headerIndex + 1);
  const byNumber = new Map(sourceRows.map(row => [row.rowNumber, row]));
  const get = (row, key) => columns[key] === undefined ? undefined : row.cells[columns[key]];
  const economics = row => {
    const otherDirectCost = columns.otherDirectCost === undefined ? knownZeroCosts ? '0' : null : exact(get(row, 'otherDirectCost'), `Autres coûts, ligne ${row.rowNumber}`);
    if (otherDirectCost === null) throw new Error('Les coûts absents ne peuvent pas être remplacés par zéro. Sélectionnez leur colonne ou confirmez qu’ils sont connus et nuls.');
    const hours = columns.estimatedHours === undefined ? '0' : exact(get(row, 'estimatedHours'), `Heures, ligne ${row.rowNumber}`);
    const laborRate = hours === '0' ? undefined : exact(get(row, 'laborRate'), `Taux horaire, ligne ${row.rowNumber}`);
    return { otherDirectCost, estimatedHours: hours, ...(laborRate === undefined ? {} : { laborRate }), weightingCoefficient: '1', laborItems: [], materials: [], subcontractingItems: [] };
  };
  let records = [], options = [];
  if (columns.kind === undefined) {
    const parsed = buildSectionsFromDpgf({ matrix, headerIndex, columns, defaultVatRate });
    if (parsed.stats.errors.length) throw new Error(parsed.stats.errors.flatMap(e => e.diagnostics.map(d => d.message)).join(' '));
    for (const [index, section] of parsed.sections.entries()) {
      records.push({ kind: 'section', key: String(section.id), parentSectionKey: section.parentSectionId == null ? null : String(section.parentSectionId), title: section.title, code: section.code, orderIndex: index });
      for (const [orderIndex, line] of section.lines.entries()) {
        if (line.lineType !== 'text' && line.unit === 'Ens') throw new Error('Une ligne simple Ens doit être corrigée explicitement dans le fichier source avant l’import v2, ou décrite comme ensemble avec ses enfants. Aucune conversion automatique.');
        const origin = line.dpgfSource, row = byNumber.get(origin?.rowNumber);
        if (line.lineType !== 'text' && !row) throw new Error('Ligne DPGF sans provenance : import composé refusé.');
        records.push({ kind: line.lineType === 'text' ? 'text' : 'simple', key: String(line.id), parentKey: null, sectionKey: String(section.id),
          description: line.description, code: line.code, unit: line.unit, quantity: String(line.quantity), orderIndex, rowNumber: origin?.rowNumber,
          priceRole: line.lineType === 'text' ? 'none' : origin?.priceBasis === 'automatic' ? 'none' : 'billed',
          ...(origin ? { unitPrice: origin.unitPrice.raw, total: origin.total.raw } : {}),
          vatRate: String(defaultVatRate), ...(line.lineType === 'text' ? {} : { economics: economics(row) }) });
      }
    }
  } else {
    if (['code', 'parentKey', 'sectionKey', 'priceRole'].some(k => columns[k] === undefined)) throw new Error('La structure composée exige les colonnes Repère, Parent, Chapitre et Rôle du prix.');
    const names = { section: 'section', chapitre: 'section', simple: 'simple', prestation: 'simple', composite: 'composite', ensemble: 'composite', text: 'text', texte: 'text' };
    const optionMap = new Map();
    for (const [orderIndex, row] of sourceRows.entries()) {
      if (!row.cells.some(cell => String(cell ?? '').trim())) continue;
      const kind = names[String(get(row, 'kind') || '').trim().toLowerCase()];
      if (!kind) throw new Error(`Nature explicite invalide, ligne ${row.rowNumber}.`);
      const key = String(get(row, 'code') ?? '').trim(), optionId = String(get(row, 'optionId') ?? '').trim();
      if (!key) throw new Error(`Repère obligatoire, ligne ${row.rowNumber}.`);
      if (optionId && !optionMap.has(optionId)) {
        const title = String(get(row, 'optionTitle') || '').trim();
        if (!title) throw new Error(`Titre de l’option ${optionId} obligatoire.`);
        optionMap.set(optionId, { id: optionId, title, selected: false, isClientVisible: true, orderIndex: optionMap.size });
      }
      const common = { kind, key, orderIndex, ...(optionId ? { optionId } : {}) };
      if (kind === 'section') records.push({ ...common, title: String(get(row, 'description') || ''), code: key, parentSectionKey: String(get(row, 'parentKey') || '').trim() || null });
      else {
        const unit = String(get(row, 'unit') || '').trim();
        if (kind === 'composite' && unit !== 'Ens') throw new Error(`L’ensemble ${key} doit porter explicitement l’unité Ens.`);
        records.push({ ...common, sectionKey: String(get(row, 'sectionKey') || '').trim(), parentKey: String(get(row, 'parentKey') || '').trim() || null,
          description: String(get(row, 'description') || ''), code: key, unit, quantity: exact(get(row, 'quantity'), `Quantité ${key}`),
          unitPrice: get(row, 'unitPrice'), total: get(row, 'total'), rowNumber: row.rowNumber,
          priceRole: String(get(row, 'priceRole') || '').trim(), vatRate: String(defaultVatRate),
          ...(kind === 'simple' ? { economics: economics(row) } : {}) });
      }
    }
    options = [...optionMap.values()];
  }
  if (!records.some(row => row.kind === 'section')) throw new Error('Aucun chapitre explicite à importer.');
  return { hierarchy: 'explicit', quantityBasis: 'local', amountBasis: 'local', records, options };
};
